import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Card, CardContent } from '../../components/ui/card';
import {
  ShoppingCart, Package, Users, TrendingUp, TrendingDown,
  AlertTriangle, DollarSign, Loader2, CreditCard, Banknote,
  Building2, Clock, ArrowUpRight, Sparkles, UtensilsCrossed,
  ClipboardList, Wrench, CalendarRange, CheckCircle2, Activity, Truck
} from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const PERIODS = [
  { id: '24h', label: 'Bugün' },
  { id: '7d', label: '1 Hafta' },
  { id: '30d', label: '1 Ay' },
  { id: '90d', label: '3 Ay' },
  { id: '365d', label: '1 Yıl' },
  { id: 'custom', label: 'Tarih Aralığı' },
];

const PERIOD_LABEL = {
  '24h': 'Son 24 Saat', '7d': 'Son 7 Gün', '30d': 'Son 30 Gün',
  '90d': 'Son 90 Gün', '365d': 'Son 365 Gün', 'custom': 'Özel Aralık'
};

export default function HesapDashboardPage() {
  const { token, user } = useHesapAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => localStorage.getItem('hesap-dash-period') || '24h');
  const [start, setStart] = useState(() => localStorage.getItem('hesap-dash-start') || '');
  const [end, setEnd] = useState(() => localStorage.getItem('hesap-dash-end') || '');

  useEffect(() => { localStorage.setItem('hesap-dash-period', period); }, [period]);
  useEffect(() => { localStorage.setItem('hesap-dash-start', start); }, [start]);
  useEffect(() => { localStorage.setItem('hesap-dash-end', end); }, [end]);

  useEffect(() => {
    const load = async () => {
      if (period === 'custom' && (!start || !end)) { setLoading(false); return; }
      setLoading(true);
      try {
        const params = new URLSearchParams({ period });
        if (period === 'custom') { params.set('start', start); params.set('end', end); }
        const res = await fetch(`${API}/dashboard/stats?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setStats(await res.json());
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [token, period, start, end]);

  const fmt = (n) => (Number(n) || 0).toLocaleString('tr-TR');
  const perms = stats?.permissions || [];
  const cards = [];

  if (stats && perms.includes('pos')) {
    cards.push(
      { title: 'Satış Cirosu', value: `${fmt(stats.sales_revenue)} TL`, sub: `${stats.sales_count || 0} işlem · ${stats.sales_items_sold || 0} ürün`, icon: ShoppingCart,
        gradient: 'from-emerald-400 to-emerald-600', shadow: 'shadow-emerald-500/30' },
      { title: 'Nakit / Kart', value: `${fmt(stats.sales_cash)} / ${fmt(stats.sales_card)} TL`, sub: 'Ödeme dağılımı', icon: Banknote,
        gradient: 'from-sky-400 to-blue-600', shadow: 'shadow-sky-500/30' }
    );
  }

  if (stats && perms.includes('musteriler')) {
    cards.push(
      { title: 'Toplam Veresiye', value: `${fmt(stats.total_veresiye)} TL`, sub: `${stats.total_customers || 0} müşteri`, icon: Users,
        gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/30' }
    );
  }

  if (stats && perms.includes('gelir_gider')) {
    cards.push(
      { title: 'Gelir', value: `${fmt(stats.income)} TL`, sub: 'Diğer gelirler', icon: TrendingUp,
        gradient: 'from-green-400 to-emerald-500', shadow: 'shadow-green-500/30' },
      { title: 'Gider', value: `${fmt(stats.expense)} TL`, sub: 'Toplam gider', icon: TrendingDown,
        gradient: 'from-rose-400 to-red-500', shadow: 'shadow-rose-500/30' },
      { title: 'Kâr', value: `${fmt(stats.profit)} TL`, sub: 'Satış + Gelir - Gider', icon: DollarSign,
        gradient: (stats.profit || 0) >= 0 ? 'from-emerald-500 to-teal-600' : 'from-red-500 to-rose-600',
        shadow: (stats.profit || 0) >= 0 ? 'shadow-emerald-500/30' : 'shadow-red-500/30' }
    );
    if ((stats.income_askida || 0) > 0) {
      cards.push(
        { title: 'Askıdaki Gelir', value: `${fmt(stats.income_askida)} TL`, sub: 'Henüz yatmamış', icon: Clock,
          gradient: 'from-amber-300 to-yellow-500', shadow: 'shadow-amber-500/30' }
      );
    }
  }

  if (stats && perms.includes('urunler')) {
    cards.push(
      { title: 'Stok Uyarısı', value: stats.low_stock_count || 0, sub: `${stats.total_products || 0} toplam ürün`,
        icon: (stats.low_stock_count || 0) > 0 ? AlertTriangle : Package,
        gradient: (stats.low_stock_count || 0) > 0 ? 'from-orange-400 to-red-500' : 'from-slate-400 to-slate-600',
        shadow: (stats.low_stock_count || 0) > 0 ? 'shadow-orange-500/30' : 'shadow-slate-500/30' }
    );
  }

  if (stats && perms.includes('apartman')) {
    cards.push(
      { title: 'Apartmanlar', value: stats.total_apartments || 0, sub: `${stats.total_units || 0} daire`, icon: Building2,
        gradient: 'from-violet-400 to-purple-600', shadow: 'shadow-violet-500/30' }
    );
  }

  // Yemek Sipariş
  if (stats && (perms.includes('yemek_siparis') || perms.includes('yemek_mutfak'))) {
    cards.push(
      { title: 'Menü Satışları', value: `${fmt(stats.food_revenue)} TL`, sub: `${stats.food_orders_count || 0} teslim · ${stats.pending_food_orders || 0} bekliyor`, icon: UtensilsCrossed,
        gradient: 'from-orange-400 to-amber-500', shadow: 'shadow-orange-500/30' }
    );
  }

  // İş Takibi
  if (stats && perms.includes('is_takibi')) {
    cards.push(
      { title: 'İş Takibi', value: stats.work_total || 0,
        sub: `${stats.work_done || 0} yapıldı · ${stats.work_pending || 0} bekliyor`,
        icon: ClipboardList,
        gradient: 'from-pink-400 to-rose-500', shadow: 'shadow-pink-500/30' }
    );
  }

  // Teknik Servis
  if (stats && perms.includes('teknik_servis')) {
    cards.push(
      { title: 'Teknik Servis', value: stats.ts_period_total || 0,
        sub: `${fmt(stats.ts_period_revenue)} TL ciro · ${stats.ts_waiting || 0} bekliyor`,
        icon: Wrench,
        gradient: 'from-indigo-400 to-blue-500', shadow: 'shadow-indigo-500/30' },
      { title: 'Onarılıyor', value: stats.ts_in_progress || 0,
        sub: `${stats.ts_completed || 0} tamamlandı · ${stats.ts_delivered || 0} teslim`,
        icon: Activity,
        gradient: 'from-yellow-400 to-orange-500', shadow: 'shadow-yellow-500/30' }
    );
  }

  const hour = new Date().getHours();
  const greeting = hour < 6 ? 'İyi geceler' : hour < 12 ? 'Günaydın' : hour < 18 ? 'İyi günler' : 'İyi akşamlar';

  return (
    <div className="space-y-5" data-testid="hesap-dashboard">
      {/* Hero hello banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 p-5 sm:p-7 shadow-xl">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-emerald-300/20 rounded-full blur-3xl" />
        <div className="relative z-10 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-yellow-300" />
              <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-100">Gösterge Paneli</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              {greeting}, {user?.full_name?.split(' ')[0] || 'Kullanıcı'}!
            </h1>
            <p className="text-emerald-100 mt-1 text-xs sm:text-sm">
              {PERIOD_LABEL[period] || 'Genel görünüm'} — İşletmenizin özeti.
            </p>
          </div>
          {stats && perms.includes('pos') && (
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl px-4 py-2.5">
              <p className="text-[10px] uppercase font-bold tracking-widest text-emerald-100">Dönem Cirosu</p>
              <p className="text-2xl sm:text-3xl font-black text-white mt-0.5">{fmt(stats.sales_revenue)} <span className="text-sm font-bold opacity-80">TL</span></p>
              <p className="text-[11px] text-emerald-100 mt-0.5 flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3" />
                {stats.sales_count || 0} işlem
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Period Selector */}
      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mr-1">
              <CalendarRange className="h-4 w-4" />
              <span className="hidden sm:inline">Dönem:</span>
            </div>
            {PERIODS.map(p => (
              <button key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  period === p.id
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/40'
                    : 'bg-slate-100 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
                data-testid={`dash-period-${p.id}`}>
                {p.label}
              </button>
            ))}
            {period === 'custom' && (
              <div className="flex items-center gap-2 ml-auto flex-wrap">
                <input type="date" value={start} onChange={e => setStart(e.target.value)}
                  className="border rounded-md px-2 py-1 text-xs h-8" data-testid="dash-custom-start" />
                <span className="text-slate-400 text-xs">→</span>
                <input type="date" value={end} onChange={e => setEnd(e.target.value)}
                  className="border rounded-md px-2 py-1 text-xs h-8" data-testid="dash-custom-end" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cards */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : cards.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-400">
            <p>Henüz aktif sayfa yetkiniz bulunmuyor.</p>
            <p className="text-sm mt-1">Yöneticinizle iletişime geçin.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {cards.map((card, i) => (
            <div key={i}
              className="group relative overflow-hidden rounded-2xl bg-white p-4 sm:p-5 border border-slate-200 hover:border-transparent transition-all hover:-translate-y-1 hover:shadow-2xl">
              <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${card.gradient} opacity-10 group-hover:opacity-25 transition-opacity blur-2xl`} />
              <div className="relative z-10 flex items-start justify-between mb-3">
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${card.gradient} ${card.shadow} shadow-lg`}>
                  <card.icon className="h-5 w-5 text-white" />
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition" />
              </div>
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide relative z-10 truncate">{card.title}</p>
              <p className="text-xl sm:text-2xl font-black text-slate-900 mt-1 relative z-10 truncate">{card.value}</p>
              <p className="text-[11px] text-slate-400 mt-1 relative z-10 truncate">{card.sub}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
